import clsx from "clsx";
import PropTypes from "prop-types";
import * as React from "react";
import { Accordion as BaseAccordion } from "@base-ui/react/accordion";
import { ExpandIcon } from "../../primitives/Icons/Icons";
import css from "./Accordion.module.css";

const AccordionRoot = React.forwardRef(
  (
    {
      value,
      defaultValue,
      onValueChange,
      multiple = false,
      disabled = false,
      orientation = "vertical",
      keepMounted = false,
      hiddenUntilFound = false,
      className,
      children,
      ...rest
    },
    ref,
  ) => (
    <BaseAccordion.Root
      ref={ref}
      className={clsx(css.root, className)}
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      multiple={multiple}
      disabled={disabled}
      orientation={orientation}
      keepMounted={keepMounted}
      hiddenUntilFound={hiddenUntilFound}
      {...rest}
    >
      {children}
    </BaseAccordion.Root>
  ),
);

AccordionRoot.displayName = "AccordionRoot";

AccordionRoot.propTypes = {
  value: PropTypes.array,
  defaultValue: PropTypes.array,
  onValueChange: PropTypes.func,
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  orientation: PropTypes.oneOf(["vertical", "horizontal"]),
  keepMounted: PropTypes.bool,
  hiddenUntilFound: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};

const AccordionItem = React.forwardRef(
  ({ value, disabled = false, className, children, ...rest }, ref) => (
    <BaseAccordion.Item
      ref={ref}
      value={value}
      disabled={disabled}
      className={clsx(css.item, className)}
      {...rest}
    >
      {children}
    </BaseAccordion.Item>
  ),
);

AccordionItem.displayName = "AccordionItem";

AccordionItem.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};

const AccordionHeader = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <BaseAccordion.Header
      ref={ref}
      className={clsx(css.header, className)}
      {...rest}
    >
      {children}
    </BaseAccordion.Header>
  ),
);

AccordionHeader.displayName = "AccordionHeader";

AccordionHeader.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const AccordionTrigger = React.forwardRef(
  ({ chevronPosition = "end", className, children, ...rest }, ref) => {
    const chevron = <ExpandIcon className={css.chevron} aria-hidden="true" />;

    return (
      <BaseAccordion.Trigger
        ref={ref}
        className={clsx(css.trigger, className)}
        data-chevron={chevronPosition}
        {...rest}
      >
        {chevronPosition === "start" && chevron}
        <span className={css.triggerContent}>{children}</span>
        {chevronPosition === "end" && chevron}
      </BaseAccordion.Trigger>
    );
  },
);

AccordionTrigger.displayName = "AccordionTrigger";

AccordionTrigger.propTypes = {
  chevronPosition: PropTypes.oneOf(["start", "end"]),
  className: PropTypes.string,
  children: PropTypes.node,
};

const AccordionPanel = React.forwardRef(
  ({ keepMounted, className, children, ...rest }, ref) => (
    <BaseAccordion.Panel
      ref={ref}
      keepMounted={keepMounted}
      className={clsx(css.panel, className)}
      {...rest}
    >
      <div className={css.panelContent}>{children}</div>
    </BaseAccordion.Panel>
  ),
);

AccordionPanel.displayName = "AccordionPanel";

AccordionPanel.propTypes = {
  keepMounted: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};

const Accordion = {
  Root: AccordionRoot,
  Item: AccordionItem,
  Header: AccordionHeader,
  Trigger: AccordionTrigger,
  Panel: AccordionPanel,
};

export default Accordion;
